import {
  Governance,
  ProgramAccount,
  Proposal,
  ProposalState,
  ProposalTransaction,
  VoteRecord,
} from "@solana/spl-governance";
import { PublicKey } from "@solana/web3.js";

import {
  OfferStatus,
  ProposalAction,
  ProposalStatus,
  ProposalTypeForProgram,
  GoveranceType,
} from "../enums/proposals.enums";
import {
  IGovernanceData,
  ITradingApprovalGovernanceData,
  IUpdateRoleWeight,
} from "./club-config.interface";
import {
  IBasicClubData,
  ISellPermissionConfig,
  IWithdrawalMembersInfo,
} from "./clubs.interface";

import { IDetailNftData, IFormField } from "./common.interface";

export interface ISplProposalWithGovernanceIndex {
  proposalAccount: ProgramAccount<Proposal>;
}

export interface IProposalWithMetaData {
  proposalBasicData: IBasicProposalData;
  proposalTransactionInfo: ProgramAccount<ProposalTransaction> | undefined;
  governanceAccount: Governance;
  metadata: ProposalMetadataType;
}

export type ProposalMetadataType = ITreasuryVaultProposalMetadata &
  IWithdrawalProposalMetadata &
  ITransferProposalMetadata &
  IUpdateGovernanceConfigProposalMetadata &
  IUpdateRoleConfigProposalMetadata &
  IAdditionalProposalInfo &
  IAddSellPermissionProposal;

export interface IProposalMetaData {
  proposalAction: ProposalAction;
}

export interface ITreasuryVaultProposalMetadata extends IProposalMetaData {
  offer: PublicKey;
  escrowedOfferedTokensAccountPda?: PublicKey;
  offeredTokenMint: PublicKey;
  wantedTokenMint: PublicKey;
  makerOfferedTokenAccount?: PublicKey;
  makerWantedTokenAccount: PublicKey;
  maker: PublicKey;
  signersBump: number;
  wantedAmount: number;
  offeredAmount: number;
  nftImgUrl: string;
  sellerFeeBps?: number;
  isMagicEdenProposal?: boolean;
  mintSymbol?: string;
  mintDecimals?: number;
}

export interface IWithdrawalProposalMetadata extends IProposalMetaData {
  withdrawalAmount: number;
  withdrawal: PublicKey;
  withdrawalData: PublicKey;
  treasuryToken: PublicKey;
  withdrawalMembersInfo: IWithdrawalMembersInfo;
  totalFinancialPower: number;
  withdrawalMint: PublicKey;
  mintSymbol: string;
  mintDecimals: number;
}

export interface ITransferProposalMetadata extends IProposalMetaData {
  proposalAction: ProposalAction;
  offer: PublicKey;
  treasuryToken: PublicKey;
  destination: PublicKey;
  amountToInvest: number;
  mint: PublicKey;
  mintLogo: string;
  mintSymbol: string;
}

export interface IUpdateGovernanceConfigProposalMetadata
  extends IProposalMetaData {
  newQuorums: number[];
  governancesToUpdate: PublicKey[];
  governedAccountsToUpdate: PublicKey[];
  newVotingTime?: number;
  governanceType: GoveranceType;
}

export interface IAddSellPermissionProposal {
  sellPermissionData: ISellPermissionConfig;
  maxVotingTime: number;
  proposalAction: ProposalAction;
}

export interface IUpdateRoleConfigProposalMetadata extends IProposalMetaData {
  roleConfigData: IUpdateRoleWeight[];
}

export interface IAdditionalProposalInfo {
  timeToVote: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null;
  hasNftSoldOnSolsea: boolean;
  offerStatus: OfferStatus;
  cancellationReason: string;
  proposalVotes: {
    yesVotePct: number;
    noVotePct: number;
    voteThresholdPct: number;
  };
  userVoteResult?: ProgramAccount<VoteRecord>;
  canUserCancel: boolean | undefined;
}

export interface IComment {
  memberAddress: string;
  description: string;
}

//TODO@milica@guta: Check this
export enum ProposalType {
  Discussion = "discussion",
  Buy = "buy",
  Sell = "sell",
}

export enum ProposalTypeNew {
  Discussion = "discussion",
  TreasuryVault = "treasury/vault",
  Withdrawal = "withdrawal",
  Transfer = "transfer",
  UpdateClubConfig = "updateClubConfig",
  AddSellPermission = "addSellPermission",
}

export enum ProposalOption {
  buyNft,
  sellNft,
}

export interface IProposalProps {
  proposal: {
    clubAddress: string;
    address: string;
    proposalName: string;
    proposalDescription: string;
    proposalType: ProposalType;
    endDate: string;
    endTime: string;
    price: number;
    priceUnit: string;
    nftAddress: string;
    nftChain: string;
    buyerAddress: string;
    status: ProposalState;
    comments: IComment[];
    approved: number;
    denied: number;
    quorum: number;
  };
}

export interface ICreateProposalForm {
  proposalTitle: string;
  proposalDescription: string;
  proposalType: ProposalTypeNew;
  rule: string;
  buyPropopsalAmount: number;
  proposalOption: ProposalAction;
  price: number | string;
  nft: IDetailNftData;
  buyerAddress: string;
  nftToBuy: string;
  magicEdenNftData: any;
  isMagicEdenNft: boolean;
  withdrawAmount: number;
  areAllDisabled: boolean;
  isNftMintValid: boolean;
  magicEdenErrors: boolean;
  transferAddress: string;
  mint: PublicKey;
  amountToInvest: number;
  newApprovalQuorum: number;
  buyOption: string;
  sellOption: string;
  sellerFeeBps: number;
  governanceType: IGovernanceData;
  newVotingTime: number;
  governanceAccounts: ITradingApprovalGovernanceData[];
  roleVotes: IUpdateRoleWeight[];
  activeTradeApprovalQuorum: ISellPermissionConfig;
  existingOffer: {
    offer: PublicKey;
    maker: PublicKey;
    price: number;
    nftMint: PublicKey;
  };
}

export interface ICreateProposalProps {
  formFields: {
    proposalTitle: IFormField;
    proposalDescription: IFormField;
    proposalType: IFormField;
    link: IFormField;
    proposalOption: IFormField;
    price: IFormField;
    nft: IFormField;
    magicEdenNftData: IFormField;
    nftToBuy: IFormField;
    buyerAddress: IFormField;
    withdrawAmount: IFormField;
    isNftMintValid: IFormField;
    isMagicEdenNft: IFormField;
    magicEdenErrors: IFormField;
    transferAddress: IFormField;
    mint: IFormField;
    amountToInvest: IFormField;
    existingOffer: IFormField;
    buyOption: IFormField;
    sellOption: IFormField;
    sellerFeeBps: IFormField;
    newApprovalQuorum: IFormField;
    governanceAccounts: IFormField;
    newVotingTime: IFormField;
    roleVotes: IFormField;
    governanceType: IFormField;
    activeTradeApprovalQuorum: IFormField;
  };
  proposalToBuy?: boolean;
  nftToSell?: IDetailNftData;
}

export interface IOffer {
  offerAddress: PublicKey;
  maker: PublicKey;
  offeredAmount: number;
  wantedTokenMint: PublicKey;
  wantedAmount: number;
  makerWantedTokenAccount: PublicKey;
  type: ProposalAction;
  offeredTokenMint: PublicKey;
  makerOfferedTokenAccount: PublicKey;
  proposalAddress: PublicKey;
  escrowedOfferedTokensAccountPda: PublicKey;
  status: OfferStatus;
  tokenLedgerMint: PublicKey;
  ledgerEntriesCount: number;
  offerTreasury: PublicKey;
  dedicatedTaker?: PublicKey;
}

export interface IProposalCancellationReason {
  id: number;
  label: string;
}

export interface ICancelledProposalData {
  address: string;
  reasonType: number;
  label: string;
}

export interface INftListingData {
  offeredAmount: number;
  wantedAmount: number;
  relatedClub: IBasicClubData;
  nftData: any;
  wantedMint: PublicKey;
  offer: PublicKey;
  maker: PublicKey;
  escrowedTokenAccount: PublicKey;
}
export interface IProposalMetadataFromProgram {
  discussionLink: string;
  treasury: PublicKey;
  currentFundraiseIndex: number;
  totalFinanacialPower: number;
  proposalIndex: number;
  governance: PublicKey;
  description: string;
  proposalType: ProposalTypeForProgram;
  proposalAction: ProposalAction;
  proposalStatus: ProposalStatus;
  proposalMetadata: PublicKey;
  voteThreshold: number;
  maxVoterWeight?: number;
  createdAt: number;
}

export interface IBasicProposalData {
  proposalAccount: ProgramAccount<Proposal>;
  proposalMetadata: IProposalMetadataFromProgram;
}

export interface ITradeInfo {
  currencyMint: PublicKey;
  price: number;
  mintSymbol: string;
  mintDecimals: number;
}

export interface INftsWithTradeInfo {
  nftMint: PublicKey;
  tradeInfo?: ITradeInfo;
}
